import type { Plant } from "../lib/plants";
import { isRemoteImageUrl } from "../lib/storage";

interface PlantCardProps {
  plant: Plant;
  onClick: () => void;
}

function daysUntil(nextWatering: string): number {
  const now = new Date();
  const next = new Date(nextWatering);
  const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const nextMidnight = new Date(next.getFullYear(), next.getMonth(), next.getDate());
  return Math.round((nextMidnight.getTime() - todayMidnight.getTime()) / (1000 * 60 * 60 * 24));
}

function healthPalette(status: string): { bg: string; fg: string } {
  const s = (status || "").toLowerCase();
  if (s.includes("heal")) return { bg: "#dcf1e2", fg: "#1f5a39" };
  if (s.includes("mild")) return { bg: "#fef3c7", fg: "#92400e" };
  if (s.includes("seri") || s.includes("sick") || s.includes("critical") || s.includes("attention"))
    return { bg: "#fee2e2", fg: "#991b1b" };
  return { bg: "#e5e7eb", fg: "#374151" };
}

function barColor(nextWatering: string): string {
  const now = Date.now();
  const next = new Date(nextWatering).getTime();
  const hoursLeft = (next - now) / (1000 * 60 * 60);
  if (hoursLeft <= 0) return "#dc2626";
  if (hoursLeft <= 24) return "#d97706";
  return "#2d7a4f";
}

function waterLabel(days: number): string {
  if (days <= 0) return "Water now";
  if (days === 1) return "Tomorrow";
  return `In ${days} days`;
}

export default function PlantCard({ plant, onClick }: PlantCardProps) {
  const health = healthPalette(plant.health_status);
  const days = daysUntil(plant.next_watering);
  const image =
    isRemoteImageUrl(plant.image_url)
      ? plant.image_url!
      : `https://placehold.co/400x400/bbf7d0/14532d/png?text=${encodeURIComponent(
          plant.nickname || plant.common_name
        )}`;

  return (
    <button type="button" onClick={onClick} className="plant-card">
      <div className="plant-card__image-wrap">
        <img src={image} alt={plant.nickname || plant.common_name} />
        <span
          className="plant-card__status"
          style={{ background: health.bg, color: health.fg }}
        >
          {(plant.health_status || "unknown").toUpperCase()}
        </span>
      </div>
      <div className="plant-card__body">
        <div className="plant-card__name">
          {plant.nickname || plant.common_name}
        </div>
        <div className="plant-card__sub">{plant.plant_type || "plant"}</div>
        <div className="plant-card__water-info">
          <span className="plant-card__water-icon">💧</span>
          <span>{waterLabel(days)}</span>
        </div>
        <div
          className="plant-card__bar"
          style={{ background: barColor(plant.next_watering) }}
        />
      </div>
      <style>{`
        .plant-card {
          display: flex;
          flex-direction: column;
          background: #ffffff;
          border: none;
          padding: 0;
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
          text-align: left;
          cursor: pointer;
          width: 100%;
          position: relative;
          z-index: 0;
        }
        .plant-card__image-wrap {
          position: relative;
          width: 100%;
          aspect-ratio: 1 / 1;
          background: #e6f0e8;
          overflow: hidden;
        }
        .plant-card__image-wrap img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .plant-card__status {
          position: absolute;
          top: 10px;
          left: 10px;
          padding: 4px 10px;
          border-radius: 999px;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 0.6px;
        }
        .plant-card__body {
          padding: 12px 14px 14px 14px;
        }
        .plant-card__name {
          font-family: Georgia, serif;
          font-weight: 700;
          font-size: 15px;
          color: #1f5a39;
          margin-bottom: 2px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .plant-card__sub {
          font-size: 11px;
          color: #6b7a6e;
          text-transform: capitalize;
        }
        .plant-card__water-info {
          display: flex;
          align-items: center;
          gap: 4px;
          margin-top: 6px;
          font-size: 11px;
          font-weight: 600;
          color: #2D7A4F;
        }
        .plant-card__water-icon {
          font-size: 12px;
        }
        .plant-card__bar {
          margin-top: 10px;
          height: 4px;
          border-radius: 2px;
          width: 100%;
        }
      `}</style>
    </button>
  );
}
